import type { SelectedModule } from '@contexts/modules-provider';
import type { SelectedOrganization } from '@contexts/organizations-provider';

import { APP_ROUTES } from '@routes/routes.constants';

import type { InitAction, InitState } from './useAppInitialization.types';

interface LoadingMessageParams {
  isModulesLoading: boolean;
  isManageModule: boolean;
  isOrganizationsLoading: boolean;
  selectedModule: SelectedModule | null;
  selectedOrganization: SelectedOrganization | null;
}

/**
 * Returns the appropriate loading message based on current initialization state
 */
export function getLoadingMessage(params: LoadingMessageParams): string {
  const {
    isModulesLoading,
    isManageModule,
    isOrganizationsLoading,
    selectedModule,
    selectedOrganization,
  } = params;

  if (isModulesLoading) return 'Loading modules...';
  if (isManageModule && isOrganizationsLoading)
    return 'Loading organizations...';
  if (selectedModule && !isManageModule)
    return `Loading ${selectedModule.name} module...`;
  if (selectedModule && selectedOrganization) return 'Loading permissions...';
  return 'Initializing application...';
}

/**
 * Returns the appropriate route for a given module and optional organization
 */
export function getModuleRoute(
  selectedModule: SelectedModule,
  selectedOrganization: SelectedOrganization | null
): string {
  if (selectedModule.name === 'Manage' && selectedOrganization) {
    return `/manage/${selectedOrganization.customerId}/home`;
  }
  if (selectedModule.name === 'Train') {
    return '/train/overview';
  }
  if (selectedModule.name === 'Instruct') {
    return APP_ROUTES.INSTRUCT_BASE;
  }
  return '/home';
}

/**
 * Pure function that determines the next initialization action based on current state.
 *
 * This is the core decision engine for app initialization. It receives a snapshot
 * of all relevant state and returns the single action to take. The hook's useEffect
 * simply executes the returned action.
 *
 * Flow:
 * 1. Deep link → process result (wait, select-module, navigate, etc.)
 * 2. Modules loading/error → wait or error page
 * 3. No modules → visitor flow (/home)
 * 4. Single module → auto-select
 * 5. Multiple modules → selector or validate
 * 6. Manage module → org sub-flow (load, select, validate, route)
 * 7. Non-Manage module → route to module
 */
export function resolveInitAction(state: InitState): InitAction {
  // ========== Phase 1: Deep Link Processing ==========
  if (state.deepLinkResult) {
    const action = resolveDeepLinkAction(state);
    return action;
  }

  // ========== Phase 2: Module Resolution ==========
  if (state.isModulesLoading) {
    return { type: 'wait' };
  }

  if (state.isModulesError) {
    return { type: 'error', errorType: 'internal-server-error' };
  }

  if (state.modules?.length === 0) {
    return { type: 'navigate', path: '/home', saveToSession: true };
  }

  // Auto-select single module
  if (state.modules?.length === 1 && !state.selectedModule) {
    return { type: 'select-module', moduleName: state.modules[0].name };
  }

  // Multiple modules: need selection
  if (state.modules && state.modules.length > 1) {
    if (!state.selectedModule) {
      return { type: 'navigate', path: '/module-selector' };
    }

    const isValidModule = state.modules.some(
      m => m.name === state.selectedModule!.name
    );
    if (!isValidModule) {
      return { type: 'navigate', path: '/module-selector' };
    }
  }

  // ========== Phase 3: Module Routing ==========
  if (!state.selectedModule) {
    return { type: 'wait' };
  }

  if (state.isManageModule) {
    const action = resolveManageOrgAction(state);
    return action;
  }

  // Non-Manage: route directly
  const route = getModuleRoute(state.selectedModule, null);
  return {
    type: 'navigate',
    path: route,
    saveToSession: true,
  };
}

/**
 * Resolves the action for deep link processing results.
 */
function resolveDeepLinkAction(state: InitState): InitAction {
  const result = state.deepLinkResult!;

  switch (result.status) {
    case 'waiting-for-modules':
    case 'waiting-for-orgs':
      return { type: 'wait' };

    case 'select-module':
      return { type: 'select-module', moduleName: result.moduleName };

    case 'select-org':
      return {
        type: 'select-org',
        orgId: result.orgId,
      };

    case 'unauthorized':
      return {
        type: 'error',
        errorType: 'not-found',
      };

    case 'navigate':
      return {
        type: 'navigate',
        path: result.path,
        saveToSession: true,
        clearDeepLink: true,
      };

    case 'no-deep-link':
    default:
      // Fall through — handled by caller as no deep link
      return { type: 'wait' };
  }
}

/**
 * Resolves the action for Manage module organization sub-flow.
 */
function resolveManageOrgAction(state: InitState): InitAction {
  if (state.isOrganizationsLoading) {
    return { type: 'wait' };
  }

  if (state.isOrganizationsError) {
    return { type: 'error', errorType: 'internal-server-error' };
  }

  if (state.organizations?.length === 0) {
    return { type: 'error', errorType: 'forbidden' };
  }

  // Auto-select single org
  if (state.organizations?.length === 1 && !state.selectedOrganization) {
    return { type: 'select-org', orgId: state.organizations[0].id };
  }

  // Multiple orgs: need selection
  if (state.organizations && state.organizations.length > 1) {
    if (!state.selectedOrganization) {
      return { type: 'navigate', path: '/organization-selector' };
    }

    const isValidOrg = state.organizations.some(
      org => org.id === state.selectedOrganization!.id
    );
    if (!isValidOrg) {
      return { type: 'navigate', path: '/organization-selector' };
    }
  }

  // Org selected: route to manage
  if (state.selectedOrganization) {
    return {
      type: 'navigate',
      path: getModuleRoute(state.selectedModule!, state.selectedOrganization),
      saveToSession: true,
    };
  }

  return { type: 'wait' };
}
